import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Loader2, CheckCircle2 } from 'lucide-react';
import { validateIndianMobile } from '../utils/phoneValidation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Small premium floor-plan preview thumbnail — Silver Woods 2 BHK 3D layout
const FLOOR_PLAN_PREVIEW = `${BACKEND_URL}/api/floorplans/novahomes/floorplans/silverwoods_2bhk_3d.jpg`;

/**
 * FloorPlanPopup — lead-gen popup for Floor Plans & Price Sheet
 *
 * Tracks: source = "Website", campaign = "Floor Plan + Price Sheet"
 *
 * Props:
 *   open        : boolean
 *   onClose     : () => void
 *   project     : optional string ("Silver Woods" | "Aurelia" | "Silver Woods & Aurelia")
 *   sourceTag   : optional source identifier (defaults to "auto-trigger")
 */
const FloorPlanPopup = ({ open, onClose, project = 'Silver Woods & Aurelia', sourceTag = 'auto-trigger' }) => {
  const [formData, setFormData] = useState({ first_name: '', phone: '' });
  const [phoneError, setPhoneError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    // reset for next open
    setTimeout(() => { setSubmitted(false); setFormData({ first_name: '', phone: '' }); setPhoneError(''); }, 200);
    onClose && onClose();
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === 'phone' && phoneError) setPhoneError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side phone validation (Option A — pattern check)
    const { valid, normalized, error } = validateIndianMobile(formData.phone);
    if (!valid) {
      setPhoneError(error || 'Please enter a valid mobile number.');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        first_name: formData.first_name,
        last_name: '',
        email: '',
        phone: normalized,
        message: `Floor Plans & Price Sheet request from ${sourceTag} (Project: ${project})`,
        property_interest: project,
        source: 'Website',
        campaign: 'Floor Plan + Price Sheet',
      });
      // Meta Pixel — Lead event
      if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', {
          content_name: 'Floor Plan + Price Sheet',
          content_category: project,
          source: sourceTag,
        });
      }
      // Google Analytics event
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'generate_lead', {
          campaign: 'Floor Plan + Price Sheet',
          project,
          source: sourceTag,
        });
      }
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      const status = err?.response?.status;
      const apiMsg = err?.response?.data?.detail;
      if (status === 429) {
        setPhoneError(apiMsg || 'Too many submissions. Please try again in a little while.');
      } else if (status === 400 && apiMsg) {
        setPhoneError(apiMsg);
      } else {
        alert('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="floorplan-popup-overlay"
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 animate-plan-overlay"
      onClick={handleClose}
    >
      <div
        data-testid="floorplan-popup"
        className="relative w-full max-w-md sm:max-w-lg animate-plan-popup max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          data-testid="floorplan-popup-close"
          aria-label="Close"
          className="absolute -top-3 -right-3 w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-xl z-10 hover:bg-gray-100 transition-colors"
        >
          <X size={16} className="text-[#111]" />
        </button>

        <div className="overflow-hidden shadow-2xl" style={{ background: '#0c0906' }}>
          {/* Floor plan preview — premium, subtle */}
          <div className="relative h-32 sm:h-36 overflow-hidden border-b border-white/[0.05]" style={{ background: '#0c0906' }}>
            <img
              src={FLOOR_PLAN_PREVIEW}
              alt="Silver Woods 2 BHK floor plan preview"
              loading="lazy"
              data-testid="floorplan-popup-preview-image"
              className="absolute inset-0 w-full h-full object-cover opacity-[0.55]"
            />
            {/* Vignette + brand-aligned dark overlay so the photo never overpowers the form */}
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(180deg, rgba(12,9,6,0.35) 0%, rgba(12,9,6,0.55) 60%, #0c0906 100%)' }}
            />
            {/* Gold caption */}
            <div className="absolute bottom-3 left-5 sm:left-7">
              <p className="text-[#C5A46D] text-[10px] uppercase tracking-[0.3em] font-medium">
                Floor Plan Preview · {project === 'Aurelia' ? 'Aurelia' : 'Silver Woods'}
              </p>
            </div>
          </div>

          {/* Header — copy */}
          <div
            className="px-6 sm:px-8 pt-6 pb-5 border-b border-white/[0.06]"
            style={{ background: 'linear-gradient(170deg, #1a1510 0%, #0f0a04 60%, #111111 100%)' }}
          >
            <h2 className="font-heading text-xl sm:text-2xl text-white font-semibold leading-tight">
              Get Silver Woods &amp; Aurelia Floor Plans + Latest Price Sheet
            </h2>
            <p className="text-white/55 text-[13px] sm:text-sm font-light leading-relaxed mt-3">
              View detailed floor plans, carpet area, current pricing and live availability directly from <span className="text-white/85 font-medium">Nova Homes</span>.
            </p>
          </div>

          {/* Body — form OR success */}
          <div className="px-6 sm:px-8 py-6 sm:py-7" style={{ background: '#0c0906' }}>
            {!submitted ? (
              <form onSubmit={handleSubmit} data-testid="floorplan-popup-form" className="space-y-3">
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder="Name"
                  required
                  autoComplete="name"
                  data-testid="floorplan-popup-name"
                  className="w-full bg-white/[0.04] border border-white/15 px-4 py-3.5 text-white placeholder:text-white/40 text-[15px] focus:outline-none focus:border-[#C5A46D]/60 transition-colors"
                />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Mobile Number"
                  required
                  maxLength={15}
                  autoComplete="tel"
                  inputMode="tel"
                  data-testid="floorplan-popup-phone"
                  className={`w-full bg-white/[0.04] border px-4 py-3.5 text-white placeholder:text-white/40 text-[15px] focus:outline-none transition-colors ${
                    phoneError ? 'border-red-400/70 focus:border-red-400' : 'border-white/15 focus:border-[#C5A46D]/60'
                  }`}
                />
                {phoneError && (
                  <p data-testid="floorplan-popup-phone-error" className="text-red-300/90 text-[12px] font-light -mt-1 pl-1">
                    {phoneError}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="floorplan-popup-submit"
                  className="w-full py-3.5 px-6 font-semibold text-sm uppercase tracking-[0.18em] transition-all duration-300 hover:brightness-110 disabled:opacity-70 flex items-center justify-center gap-2"
                  style={{
                    background: 'linear-gradient(135deg, #D4AF37, #F5E6C4, #D4AF37)',
                    color: '#0f0a04',
                    boxShadow: '0 4px 18px rgba(212,175,55,0.22)'
                  }}
                >
                  {isSubmitting && <Loader2 size={16} className="animate-spin" />}
                  {isSubmitting ? 'Sending…' : 'Get Instant Access'}
                </button>
                <p className="text-white/35 text-[11px] font-light text-center pt-1 tracking-wide">
                  Direct from Nova Homes Sales Team • Current Pricing • Live Availability
                </p>
              </form>
            ) : (
              <div data-testid="floorplan-popup-success" className="text-center py-4">
                <div className="w-14 h-14 mx-auto rounded-full bg-[#C5A46D]/15 text-[#C5A46D] flex items-center justify-center mb-4">
                  <CheckCircle2 size={28} />
                </div>
                <h3 className="font-heading text-xl sm:text-2xl text-white font-semibold mb-3">Thank you.</h3>
                <p className="text-white/65 text-sm font-light leading-relaxed mb-6">
                  Our team will share the floor plans, latest pricing and current availability shortly.
                </p>
                <button
                  type="button"
                  onClick={handleClose}
                  data-testid="floorplan-popup-success-close"
                  className="text-[#C5A46D] text-xs uppercase tracking-[0.25em] font-medium hover:text-[#D4AF37] transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloorPlanPopup;
